/**
 * 播放器事件管理器
 * 参考原项目 tv-linux 的 playerEvent 方法
 * 封装播放器事件的监听和清理，符合最佳开发实践
 */

import { usePlayerStore, type DefinitionItem, PLAYER_STORAGE_KEYS } from '@/stores/playerStore';
import { logger } from '@/utils/devUtils';
import type { SubtitleItem } from '@/types/subtitle';
import { getErrorInfo } from '@/utils/playerErrorCodes';
import { isIflixPlatform } from './platformUtils';
import { getPlayUrl } from '@/services/playApi';
import { INTERNAL_TO_DISPLAY_QUALITY_MAP } from '@/utils/constants';
import { toast } from '@/components/common';
import i18n from '@/i18n/config';
import { getCookieAmend } from '@/utils/apiUtils';
import { shouldSkipOutro } from '@/utils/playerUtils';

/**
 * 播放器事件类型
 * 参考 @tencent/wetv-kernel 播放器的事件定义
 */
export interface PlayerEventMap {
  // 基础播放事件（使用播放器事件名）
  'video:playing': () => void;
  'video:pause': () => void;
  'video:buffering': () => void;
  'video:canplay': () => void;
  'video:error': (error?: any) => void;

  // 时间相关事件
  'video:timeupdate': (data?: any) => void;
  'video:seeking': () => void;
  'video:seeked': () => void;

  // 播放会话事件
  'session:start': () => void;
  'session:end': (data?: { code?: string; endState?: string; isUserStop?: boolean }) => void;

  // 视频信息事件
  'video:req-info-end': (data?: any) => void;

  // 视频加载事件
  'video:load-start': () => void;
  'video:load-progress': (data?: any) => void;
  'video:duration-change': (data?: any) => void;
  'video:metadata-change': (data?: any) => void;

  // 清晰度切换事件
  'video:setlevel-start': () => void;
  'video:setlevel-end': () => void;

  // 倍速切换事件
  'video:rate-change': (data?: { rate?: number }) => void;

  // 播放器尺寸变化
  resize: () => void;

  // 播放状态变化
  statechange: (data?: any) => void;
}

/**
 * 事件处理器类型
 */
type EventHandler<T extends keyof PlayerEventMap> = PlayerEventMap[T];

/**
 * 播放器事件管理器
 */
export class PlayerEventManager {
  private player: any | null = null;
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private isInitialized = false;
  private timeUpdateInterval: NodeJS.Timeout | null = null;
  private isOutroSkipped = false; // 标记是否已跳过片尾，防止重复显示 toast

  /**
   * 初始化事件管理器
   * @param player 播放器实例
   */
  init(player: any): void {
    if (!player || typeof player.on !== 'function') {
      logger.warn('[player] 播放器实例无效或不支持事件监听');
      return;
    }

    this.player = player;
    this.isInitialized = true;
    this.isOutroSkipped = false; // 重置片尾跳过标记

    // 同步播放器初始状态
    this.syncInitialState(player);

    // 注册默认事件处理器
    this.registerDefaultHandlers();
  }

  /**
   * 监听播放器事件
   * @param event 事件名称
   * @param handler 事件处理器
   * @returns 取消监听的函数
   */
  on<T extends keyof PlayerEventMap>(event: T, handler: EventHandler<T>): () => void {
    if (!this.player || !this.isInitialized) {
      logger.warn(`[player] 播放器未初始化，无法监听事件: ${String(event)}`);
      return () => {};
    }

    // 标准化事件名称（支持多种命名格式）
    const normalizedEvent = this.normalizeEventName(event);

    // 注册到播放器
    try {
      this.player.on(normalizedEvent, handler);
    } catch (error) {
      logger.error(`[player] 注册事件失败: ${String(event)}`, error);
      return () => {};
    }

    // 保存处理器引用（用于清理）
    if (!this.eventHandlers.has(normalizedEvent)) {
      this.eventHandlers.set(normalizedEvent, new Set());
    }
    this.eventHandlers.get(normalizedEvent)!.add(handler);

    // 返回取消监听的函数
    // 注意：使用原始 event 类型，off 方法内部会进行标准化
    return () => {
      this.off(event as T, handler);
    };
  }

  /**
   * 取消监听播放器事件
   * @param event 事件名称
   * @param handler 事件处理器（可选，不提供则移除该事件的所有处理器）
   */
  off<T extends keyof PlayerEventMap>(event: T, handler?: EventHandler<T>): void {
    if (!this.player) return;

    const normalizedEvent = this.normalizeEventName(event);

    try {
      if (handler) {
        // 移除特定处理器
        this.player.off(normalizedEvent, handler);
        const handlers = this.eventHandlers.get(normalizedEvent);
        if (handlers) {
          handlers.delete(handler);
        }
      } else {
        // 移除该事件的所有处理器
        const handlers = this.eventHandlers.get(normalizedEvent);
        if (handlers) {
          handlers.forEach((h) => {
            this.player?.off(normalizedEvent, h);
          });
          handlers.clear();
        }
      }
    } catch (error) {
      logger.error(`[player] 取消事件监听失败: ${String(event)}`, error);
    }
  }

  /**
   * 清理所有事件监听
   */
  cleanup(): void {
    // 停止定时器
    this.stopTimeUpdateInterval();

    if (!this.player) return;

    // 移除所有事件监听
    this.eventHandlers.forEach((handlers, event) => {
      handlers.forEach((handler) => {
        try {
          this.player?.off(event, handler);
        } catch (error) {
          logger.error(`[player] 清理事件失败: ${event}`, error);
        }
      });
    });

    // 清空处理器映射
    this.eventHandlers.clear();
    this.player = null;
    this.isInitialized = false;
    this.isOutroSkipped = false; // 重置片尾跳过标记
  }

  /**
   * 处理跳过片尾逻辑
   * 优化：只在接近片尾区域时检查，提高性能
   * @param currentTime 当前播放时间
   */
  private handleSkipOutro(currentTime: number): void {
    // 如果已经跳过，直接返回
    if (this.isOutroSkipped) return;

    const { skipEnd, skipTailEnd, skipIntroEnabled, totalTime } = usePlayerStore.getState();

    // 如果未启用跳过功能，直接返回
    if (!skipIntroEnabled) return;

    // 如果总时长为0或片尾数据无效，直接返回
    if (totalTime === 0 || skipEnd <= 0) return;

    // 性能优化：只在接近片尾区域时检查（片尾开始前1秒开始检查）
    // 这样可以避免在视频前半部分频繁检查
    const tailTime = totalTime - skipEnd;
    const tailTimeEnd = totalTime - skipTailEnd;
    const checkStartTime = tailTime - 1;
    if (currentTime < checkStartTime) return;
    // 将绝对时间点转换为从结束往前数的时间
    // skipEnd 是片尾开始的绝对时间点，转换为从结束往前数：totalTime - skipEnd
    // skipTailEnd 是片尾结束的绝对时间点，转换为从结束往前数：totalTime - skipTailEnd
    // 开始判断前，把数据都打印出来，便于排查问题
    logger.log('[player][handleSkipOutro] 参数:', {
      currentTime,
      skipEnd,
      skipIntroEnabled,
      totalTime,
      tailTime,
      tailTimeEnd,
      isOutroSkipped: this.isOutroSkipped,
    });
    if (shouldSkipOutro(currentTime, totalTime, tailTime, this.isOutroSkipped, skipIntroEnabled)) {
      // 显示跳过片尾的 toast 提示
      toast.show(i18n.t('player.skipOutroToast'));
      this.isOutroSkipped = true; // 标记已跳过片尾，防止重复显示
      this.player.seek(totalTime - skipTailEnd); // 跳转到视频末尾
    }
  }

  /**
   * 启动时间更新定时器（备用方案）
   */
  private startTimeUpdateInterval(): void {
    // 清除之前的定时器
    this.stopTimeUpdateInterval();

    // 每250ms更新一次（与HTML5 video的timeupdate事件频率类似）
    this.timeUpdateInterval = setInterval(() => {
      if (this.player?.videoElement) {
        const { currentTime, paused } = this.player.videoElement;
        if (currentTime >= 0 && isFinite(currentTime)) {
          const store = usePlayerStore.getState();
          if (!paused) {
            // 使用 setCurrentTime 方法，它会应用跳过片头逻辑
            store.setCurrentTime(currentTime);
          }
        }
      }
    }, 250);
  }

  /**
   * 停止时间更新定时器
   */
  private stopTimeUpdateInterval(): void {
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
      this.timeUpdateInterval = null;
    }
  }

  /**
   * 检查是否已初始化
   */
  get initialized(): boolean {
    return this.isInitialized;
  }

  /**
   * 同步播放器初始状态
   * 在初始化时检查播放器的实际状态并同步到store
   */
  private syncInitialState(player: any): void {
    try {
      // 同步播放状态
      logger.log('[player] 同步初始状态', { hasVideoElement: !!player.videoElement });
      if (player.videoElement) {
        // 播放状态由播放器内部 currentVideoInfo.state 维护，此处仅同步时间

        // 同步当前时间
        const currentTime = player.videoElement.currentTime || 0;
        if (currentTime > 0) {
          usePlayerStore.getState().setCurrentTime(currentTime);
        }
        // 同步总时长（重要：即使为0也要尝试获取）
        const duration = player.videoElement.duration || 0;
        if (duration > 0) {
          usePlayerStore.getState().setTotalTime(duration);
          logger.log('[player] 初始总时长:', { duration });
        } else {
          // 如果初始时长为0，延迟尝试获取（视频可能还在加载）
          setTimeout(() => {
            const delayedDuration = player.videoElement?.duration || 0;
            if (delayedDuration > 0) {
              usePlayerStore.getState().setTotalTime(delayedDuration);
              logger.log('[player] 延迟获取总时长:', { duration: delayedDuration });
            }
          }, 1000);
        }
      }
      // 播放状态由播放器内部 currentVideoInfo.state 维护，无需在此同步
    } catch (error) {
      logger.warn('[player] 同步初始状态失败:', error);
    }
  }

  /**
   * 注册默认事件处理器
   * 这些是播放器状态管理必需的基础事件
   */
  private registerDefaultHandlers(): void {
    if (!this.player) return;

    // 播放状态由播放器内部 currentVideoInfo.state 维护，此处仅触发 UI 刷新
    this.on('video:playing', () => {
      usePlayerStore.getState().setPlaybackStateVersion();
      this.startTimeUpdateInterval();
    });

    this.on('video:pause', () => {
      usePlayerStore.getState().setPlaybackStateVersion();
      this.stopTimeUpdateInterval();
    });

    this.on('video:buffering', () => {
      usePlayerStore.getState().setPlaybackStateVersion();
    });

    this.on('video:error', () => {
      usePlayerStore.getState().setPlaybackStateVersion();
    });

    // 播放状态变化事件 使用 statechange 事件名
    this.on('statechange', (data?: Record<string, any>) => {
      // 使用类型别名，移除多余 interface 声明，提高可读性
      type StateChangeData = {
        type: string;
        target: unknown;
        data: {
          videoInfo?: unknown;
          new?: string;
          old?: string;
          isContiguous?: boolean;
          [key: string]: any;
        };
        [key: string]: any;
      };

      const typedData = data as StateChangeData;
      logger.log('[player] statechange', typedData);

      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('player:statechange', {
            detail: typedData,
          })
        );
      }
    });

    // 时间更新
    this.on('video:timeupdate', (data?: any) => {
      // logger.log('[player] timeupdate', data);
      // 处理 data.data.playtime，支持所有时间值（包括0）
      const { playtime: currentTime = 0 } = data?.data || {};
      // 移除 currentTime > 0 的限制，允许更新0值，确保进度条能正确显示初始状态
      if (currentTime >= 0 && isFinite(currentTime)) {
        const { previewTime, videoInfo } = usePlayerStore.getState();

        // 超出预览时间 提示（需要检查 videoInfo 是否存在）
        if (
          previewTime > 0 &&
          currentTime > previewTime &&
          videoInfo?.resData &&
          (videoInfo.resData.exem > 0 || videoInfo.resData.vl?.vi?.[0]?.st === 8)
        ) {
          logger.log('[player] 超出预览时间，停止播放');
          const { payStatus } = usePlayerStore.getState();
          usePlayerStore.getState().setIsOnError(true);
          this.player.stop();
          if (payStatus === 5 || payStatus === 6) {
            usePlayerStore.getState().setError(-1, 'play', {
              message: 'previewVip',
              errType: 'vip',
            });
          } else if (payStatus === 7 || payStatus === 9) {
            usePlayerStore.getState().setError(-1, 'play', {
              message: 'previewBuy',
              errType: 'nobuy',
            });
          } else {
            usePlayerStore.getState().setError(-1, 'play', {
              message: isIflixPlatform() ? 'previewLimitIflix' : 'previewLimitWetv',
              errType: 'noLimit',
            });
          }
          return; // 超出预览时间后不再更新进度
        }
        usePlayerStore.getState().setCurrentTime(currentTime);
      }

      // 处理跳过片尾逻辑（使用优化后的方法）
      this.handleSkipOutro(currentTime);
    });

    // 跳转完成事件（用户手动跳转时也需要检查跳过片尾）
    this.on('video:seeked', () => {
      if (this.player?.videoElement?.currentTime !== undefined) {
        const { currentTime } = this.player.videoElement;
        // 用户手动跳转后，也需要检查是否需要跳过片尾
        this.handleSkipOutro(currentTime);
      }
    });

    // 视频可以播放事件（合并了时长更新逻辑）
    this.on('video:canplay', (data?: any) => {
      usePlayerStore.getState().setPlaybackStateVersion();
      this.startTimeUpdateInterval();

      // 尝试从 videoElement 获取时长
      if (this.player?.videoElement?.duration) {
        const { duration } = this.player.videoElement;
        if (duration > 0 && isFinite(duration)) {
          usePlayerStore.getState().setTotalTime(duration);
        }
      }

      logger.log('[player] 视频可以播放', {
        videoInfo: data?.data?.videoInfo,
        configInner: data?.data?.videoInfo?.configInner,
        extraVarsInner: data?.data?.videoInfo?.extraVarsInner,
      });
    });

    // 视频加载开始
    this.on('video:load-start', () => {
      logger.log('[player] 开始加载正片');
      usePlayerStore.getState().resetError();
    });

    // 视频加载进度
    this.on('video:load-progress', (data?: any) => {
      const bufferRange = data?.data?.bufferRange;
      logger.log('[player] 视频加载进度', {
        bufferRange,
        bufferCount: bufferRange?.length || 0,
      });
    });

    // 视频时长变化 - 仅打印日志，不再设置 totalTime（已从 VIDEO_REQ_INFO_END 获取）
    this.on('video:duration-change', (data?: any) => {
      const duration = data?.data?.duration;
      logger.log('[player] 视频时长变化', {
        duration,
        rawData: data?.data,
        note: '总时长已从 VIDEO_REQ_INFO_END 事件获取，此处仅记录',
      });
    });

    // 视频元数据变化
    this.on('video:metadata-change', (data?: any) => {
      const width = data?.data?.width;
      const height = data?.data?.height;
      logger.log('[player] 视频元数据变化', {
        width,
        height,
        aspectRatio: width && height ? (width / height).toFixed(2) : undefined,
        rawData: data?.data,
      });
    });

    // 播放会话结束 - 处理播放结束和自动播放下一集
    this.on('session:end', (data?: any) => {
      try {
        // 打印完整的事件数据用于调试
        logger.log('[player] 收到播放结束事件，完整数据:', {
          data,
          dataData: (data as any)?.data,
        });

        // 从 data.data 或 data 中获取字段
        const eventData = (data as any)?.data || data || {};
        const { code, endState, isUserStop } = eventData;

        logger.log('[player] 提取的字段:', {
          code,
          endState,
          isUserStop,
        });

        // 判断是否是正常播放结束
        // 参考 tv-linux/src/views/newParticular/newParticular.js:5343-5375
        // code === '0' 或 code === 0 表示正常结束
        // endState === 'normal' 表示正常播放结束
        // endState === 'interrupt' 表示被中断（切换视频时触发），不应自动播放下一集
        const isCodeNormal = code === '0' || code === 0 || code === undefined;
        const isEndStateNormal = endState === 'normal';
        const isNotUserStop = !isUserStop;
        const isNormalEnd = isCodeNormal && isEndStateNormal && isNotUserStop;

        logger.log('[player] 判断结果:', {
          isNormalEnd,
          isCodeNormal,
          isEndStateNormal,
          isNotUserStop,
          endState,
          code,
          isUserStop,
        });

        if (isNormalEnd) {
          // 正常播放结束，自动播放下一集
          logger.log('[player] 正常播放结束，准备自动播放下一集');

          // 重置片尾跳过标记，为下一集做准备
          this.isOutroSkipped = false;

          // 延迟一小段时间再播放下一集，确保当前视频完全结束
          setTimeout(() => {
            const result = usePlayerStore.getState().playNext();
            logger.log('[player] playNext 返回结果:', result);
            if (result?.success) {
              // 触发事件通知组件处理（路由导航或显示提示）
              if (typeof window !== 'undefined') {
                logger.log('[player] 触发 player:next 事件');
                window.dispatchEvent(
                  new CustomEvent('player:next', {
                    detail: result,
                  })
                );
              }
            } else {
              // 没有下一集，触发全局事件以展示重新播放弹层
              logger.log('[player] 没有下一集，触发 player:noNext 事件', result);
              if (typeof window !== 'undefined') {
                window.dispatchEvent(
                  new CustomEvent('player:noNext', {
                    detail: result ?? { type: 'noNext' },
                  })
                );
              }
            }
          }, 500); // 延迟500ms，确保播放器状态已更新
        } else {
          // 非正常结束（用户手动停止或错误），不自动播放下一集
          // 处理播放报错 errorCode 和 errorMessage
          // 参考 tv-linux/src/views/newParticular/newParticular.js:5384-5484
          const { code: errMsg } = eventData;
          // 如果有错误码且不为 '0' 且不为 0，处理错误
          if (errMsg && errMsg !== '0') {
            usePlayerStore.getState().setIsOnError(true);
            logger.log('[player] 非正常结束，检测到错误码:', {
              errMsg,
              endState,
              isUserStop,
            });
            const bizCodeReg = /70013/i;
            const errorCode = `${usePlayerStore.getState().cid}_${usePlayerStore.getState().vid}_[${errMsg}]_${this.player.getCommonKv('flowid') || ''}`;
            const isBiz = bizCodeReg.test(errMsg);
            if (!isBiz) {
              usePlayerStore.getState().setError(errorCode, 'session', {
                errType: 'normal',
                message: 'networkError',
              });
            }
            const bizCodeInfo = errMsg.replace(bizCodeReg, '').split('.');
            const code = Number(bizCodeInfo[0]);
            const exem = Number(bizCodeInfo[1]);

            if (code === 80 && exem === 20) {
              // 年龄限制 登录 非登录
            } else if (code === 94) {
              usePlayerStore.getState().setError(code, 'session', {
                errCode: code,
                errType: 'accountCrackdown',
                message: data.message || '',
                playErrCode: code,
              });
            } else if (code === 91 || code === 83 || (code === 80 && exem === 9)) {
              // 预鉴权...
              processTVPreAuth(usePlayerStore.getState().vid || '', usePlayerStore.getState().cid || '', errMsg);
            } else {
              // 根据错误码获取常规错误信息
              const errorInfo = getErrorInfo(code, exem);
              // 更新播放器状态
              usePlayerStore.getState().setError(code, 'session', errorInfo);
            }
          } else {
            // 没有错误码，只是非正常结束（可能是用户手动停止）
            logger.log('[player] 非正常结束，不自动播放下一集', {
              code,
              endState,
              isUserStop,
            });
          }
        }

        usePlayerStore.getState().setPlaybackStateVersion();
        // 将字幕清空
        const subtitlePlugin = this.player.plugins.subtitle;
        subtitlePlugin.clearTextTrack();
      } catch (error) {
        logger.error('[player] SESSION_END 处理失败:', error);
      }
    });

    // 视频信息请求结束 - 处理视频信息和字幕初始化
    this.on(
      'video:req-info-end',
      (data?: {
        data?: {
          data?: {
            parseData?: {
              title?: string;
              duration?: number;
              subtitleList?: SubtitleItem[];
              definitionList?: DefinitionItem[];
              [key: string]: unknown;
            };
            resData: {
              em?: number;
              preview?: number;
            };
            [key: string]: unknown;
          };
          [key: string]: unknown;
        };
        [key: string]: unknown;
      }) => {
        try {
          logger.log('[player] VIDEO_REQ_INFO_END error', {
            data: JSON.stringify(data?.data),
          });
          const vInfoData = data?.data?.data;
          const parseData = vInfoData?.parseData;
          const previewTime = vInfoData?.resData?.preview;

          // 开发环境日志
          logger.log('video:PlayerEvent [VIDEO_REQ_INFO_END]', {
            hasVInfoData: !!vInfoData,
            hasParseData: !!parseData,
            title: parseData?.title,
            duration: parseData?.duration,
            subtitleCount: parseData?.subtitleList?.length || 0,
            definitionCount: parseData?.definitionList?.length || 0,
          });

          // 试看逻辑
          if (vInfoData?.resData.em !== 0) {
            // 无法试看
            usePlayerStore.getState().setPreviewTime(0);
          }

          if (!parseData) {
            logger.warn('[player] VIDEO_REQ_INFO_END parseData 不存在');
            return;
          }

          // 保存 videoInfo 到 store（保存完整的 vInfoData，包含 resData）
          usePlayerStore.getState().setVideoDetailInfo({
            ...parseData,
            resData: vInfoData?.resData, // 保存 resData 用于判断试看类型
          });

          // 设置视频标题
          if (parseData.title) {
            const { cid, vid } = usePlayerStore.getState();
            if (cid && vid) {
              usePlayerStore.getState().setVideoInfo(cid, vid, parseData.title);
              logger.log('video:PlayerEvent [VIDEO_REQ_INFO_END] 视频标题已设置', { title: parseData.title });
            }
          }

          // 设置视频总时长（如果有 previewTime 则使用 previewTime，否则从 parseData.duration 获取，单位：秒）
          if (previewTime) {
            usePlayerStore.getState().setPreviewTime(previewTime);
          }
          if (previewTime && previewTime > 0 && isFinite(previewTime)) {
            usePlayerStore.getState().setTotalTime(previewTime);
            logger.log('video:PlayerEvent [VIDEO_REQ_INFO_END] 总时长已设置（试看时长）', {
              duration: previewTime,
              unit: '秒',
            });
          } else if (parseData.duration && parseData.duration > 0 && isFinite(parseData.duration)) {
            usePlayerStore.getState().setTotalTime(parseData.duration);
            logger.log('video:PlayerEvent [VIDEO_REQ_INFO_END] 总时长已设置', {
              duration: parseData.duration,
              unit: '秒',
            });
          }

          // 初始化字幕列表
          const { subtitleList } = parseData;
          if (subtitleList && subtitleList.length > 0) {
            const channelId =
              typeof window !== 'undefined' ? localStorage.getItem('v_channel_id') || undefined : undefined;
            usePlayerStore.getState().initSubtitleList(subtitleList, channelId);
            logger.log('video:PlayerEvent [VIDEO_REQ_INFO_END] 字幕列表已初始化', {
              subtitleCount: subtitleList.length,
              channelId,
            });
          } else {
            // 如果没有字幕列表，清空之前的字幕状态
            usePlayerStore.getState().clearSubtitleState();
            logger.log('video:PlayerEvent [VIDEO_REQ_INFO_END] 无字幕列表，已清空字幕状态');
          }

          // 清晰度列表已保存在 parseData.definitionList 中，可通过 videoInfo.definitionList 访问
          if (parseData.definitionList && parseData.definitionList.length > 0) {
            logger.log('video:PlayerEvent [VIDEO_REQ_INFO_END] 清晰度列表已保存', {
              definitionCount: parseData.definitionList.length,
              definitions: parseData.definitionList.map((d) => ({
                name: d.name,
                cname: d.cname,
                resolution: d.resolution,
              })),
            });

            // 根据用户设置设置初始清晰度（使用内部格式）
            const playerStore = usePlayerStore.getState();
            const savedQuality =
              typeof window !== 'undefined' ? localStorage.getItem(PLAYER_STORAGE_KEYS.QUALITY) : null;
            const validInternalQualities = Object.keys(INTERNAL_TO_DISPLAY_QUALITY_MAP);

            if (savedQuality && validInternalQualities.includes(savedQuality.toLowerCase())) {
              let targetQuality = savedQuality.toLowerCase();

              // 检查VIP权限：如果清晰度是1080P（fhd）但用户不是VIP，则降级到720P（shd）
              if (targetQuality === 'fhd') {
                const vipStatus = getCookieAmend('status');
                const isVip = vipStatus === '3'; // status: 1=游客, 2=登录非会员, 3=会员

                if (!isVip) {
                  // 用户不是VIP，降级到720P
                  targetQuality = 'shd';
                  // 更新localStorage中的清晰度设置
                  if (typeof window !== 'undefined') {
                    localStorage.setItem(PLAYER_STORAGE_KEYS.QUALITY, targetQuality);
                  }
                  logger.log('[player] 用户不是VIP，清晰度从1080P降级到720P', {
                    savedQuality,
                    targetQuality,
                  });
                }
              }

              // 检查目标清晰度是否在可用列表中（通过内部格式 name 匹配）
              const qualityExists = parseData.definitionList.some((def: DefinitionItem) => def.name === targetQuality);

              if (qualityExists) {
                // 先更新 store 中的清晰度状态（确保 UI 显示正确）
                const currentState = playerStore;
                if (currentState.quality !== targetQuality) {
                  // 调用 setQuality 更新状态和切换清晰度
                  currentState.setQuality({ quality: targetQuality }).catch((error) => {
                    // 如果播放器实例还未初始化，setQuality 会返回失败
                    // 但至少我们已经更新了 store 状态，UI 会显示正确的清晰度
                    logger.warn('[player] 设置初始清晰度失败（可能播放器未初始化）:', error);
                  });
                  logger.log('video:PlayerEvent [VIDEO_REQ_INFO_END] 已根据用户设置设置初始清晰度', {
                    savedQuality,
                    targetQuality,
                    playerInstanceExists: !!currentState.playerInstance,
                  });
                }
              } else {
                logger.warn('[player] 用户设置的清晰度不在可用列表中', {
                  savedQuality,
                  targetQuality,
                  availableQualities: parseData.definitionList.map((d: DefinitionItem) => d.name),
                });
              }
            }
          }
        } catch (error) {
          logger.error('[player] VIDEO_REQ_INFO_END 处理失败:', error);
        }
      }
    );
  }

  /**
   * 标准化事件名称
   * 将播放器事件名映射到播放器事件常量
   */
  private normalizeEventName(event: string): string {
    // 如果播放器支持事件常量，尝试使用常量
    if (this.player && (window as any)?.ThumbPlayer) {
      const { H5_PLAY_EVENT, PLAY_EVENT } = (window as any).ThumbPlayer;

      // 映射到 H5_PLAY_EVENT 常量
      const h5EventMap: Record<string, string> = {
        'video:req-info-end': H5_PLAY_EVENT?.VIDEO_REQ_INFO_END,
        'video:playing': H5_PLAY_EVENT?.VIDEO_PLAYING,
        'video:pause': H5_PLAY_EVENT?.VIDEO_PAUSE,
        'video:timeupdate': H5_PLAY_EVENT?.VIDEO_TIMEUPDATE,
        'video:seeking': H5_PLAY_EVENT?.VIDEO_SEEKING,
        'video:seeked': H5_PLAY_EVENT?.VIDEO_SEEKED,
        'session:end': H5_PLAY_EVENT?.PLAY_SESSION_END,
        'session:start': H5_PLAY_EVENT?.PLAY_SESSION_START,
        'video:canplay': H5_PLAY_EVENT?.VIDEO_CANPLAY,
        'video:buffering': H5_PLAY_EVENT?.VIDEO_BUFFERING,
        'video:error': H5_PLAY_EVENT?.VIDEO_ERROR,
        'video:setlevel-start': H5_PLAY_EVENT?.VIDEO_SETLEVEL_START,
        'video:setlevel-end': H5_PLAY_EVENT?.VIDEO_SETLEVEL_END,
        'video:rate-change': H5_PLAY_EVENT?.VIDEO_RATE_CHANGE,
        'video:load-start': H5_PLAY_EVENT?.VIDEO_LOAD_START,
        'video:load-progress': H5_PLAY_EVENT?.VIDEO_LOAD_PROGRESS,
        'video:duration-change': H5_PLAY_EVENT?.VIDEO_DURATION_CHANGE,
        'video:metadata-change': H5_PLAY_EVENT?.VIDEO_METADATA_CHANGE,
      };

      // 映射到 PLAY_EVENT 常量
      const playEventMap: Record<string, string> = {
        resize: PLAY_EVENT?.RESIZE,
        statechange: PLAY_EVENT?.STATE_CHANGE,
      };

      if (h5EventMap[event] || playEventMap[event]) {
        return h5EventMap[event] || playEventMap[event] || event;
      }
    }

    // 默认返回原事件名称
    return event;
  }
}

// 创建全局单例
let eventManagerInstance: PlayerEventManager | null = null;

/**
 * 获取播放器事件管理器实例（单例模式）
 */
export function getPlayerEventManager(): PlayerEventManager {
  if (!eventManagerInstance) {
    eventManagerInstance = new PlayerEventManager();
  }
  return eventManagerInstance;
}

/**
 * 重置事件管理器（用于测试或重新初始化）
 */
export function resetPlayerEventManager(): void {
  if (eventManagerInstance) {
    eventManagerInstance.cleanup();
    eventManagerInstance = null;
  }
}

/**
 * 播放海外预鉴权处理逻辑
 */
export async function processTVPreAuth(vid: string, cid: string, dataCode: string) {
  let { payStatus } = usePlayerStore.getState();
  // 解决时序问题：如果 payStatus 为初始值 -1，尝试重新获取
  if (payStatus === -1) {
    try {
      logger.log('[player] processTVPreAuth payStatus 为 -1，尝试重新获取播放详情');
      const response = await getPlayUrl(cid, vid);
      // 兼容处理：尝试从 data 中获取，或者直接从 response 中获取
      // @ts-ignore
      const playDetailData = response.data || response;
      // @ts-ignore
      const currentVideoData = playDetailData?.video_data?.current_video_data;

      if (currentVideoData) {
        const { payment_type: newPayStatus = 0 } = currentVideoData;
        logger.log('[player] processTVPreAuth 重新获取 payStatus 成功:', newPayStatus);
        // 更新 store
        usePlayerStore.getState().setPayStatus(newPayStatus);
        payStatus = newPayStatus;
      } else {
        logger.warn('[player] processTVPreAuth 重新获取播放详情成功但未找到 video_data');
      }
    } catch (error) {
      logger.error('[player] processTVPreAuth 重新获取播放详情失败:', error);
    }
  }

  if (payStatus === -1) {
    usePlayerStore.getState().setError(dataCode, 'session', {
      errType: 'normal',
      message: 'networkError',
    });
    return;
  }
  if (payStatus === 5 || payStatus === 6) {
    usePlayerStore.getState().setError(dataCode, 'session', {
      errType: 'vip',
      message: 'activeVipTip',
    });
    return;
  }
  if (payStatus === 7 || payStatus === 9) {
    usePlayerStore.getState().setError(dataCode, 'session', {
      errType: 'nobuy',
      message: isIflixPlatform() ? 'noBuyIflix' : 'noBuy',
    });
    return;
  }
  usePlayerStore.getState().setError(dataCode, 'session', {
    errType: 'noLimit',
    message: isIflixPlatform() ? 'noLimitIflix' : 'noLimit',
  });
}
